import { omit } from 'lodash';
import { useActiveJourney } from 'shared/store/journey-slice';
import {
  Journey,
  JourneyGraph,
  JourneyGraphNodeQuestion,
  JourneyNodeAction,
  JourneyQuestionConfigMap,
  JourneyQuestionType,
  JourneySplitTestConfigUpdate,
  JourneyUpdate,
  PartialJourneyWithId,
} from 'types/journey';
import {
  deleteUnknownNonLeafNode,
  isBranchNode,
  isInboundChatNode,
  isQuestionNode,
  scrubTemplateGraph,
} from './graph';

export const JOURNEY_QUESTION_DEFAULT_LABEL = {
  [JourneyQuestionType.email]: 'Email address',
  [JourneyQuestionType.phone]: 'Phone number',
} as Record<JourneyQuestionType, string | undefined>;

export const JOURNEY_QUESTION_DEFAULT_KEY = {
  [JourneyQuestionType.email]: 'email',
  [JourneyQuestionType.phone]: 'phone',
} as Record<JourneyQuestionType, string | undefined>;

/**
 * Returns string error message if invalid, undefined if valid.
 */
export function validateJourneyQuestion(
  question: JourneyGraphNodeQuestion,
): string | undefined {
  if (!question.message) {
    return 'Message is a required field';
  }
  if (question.type === JourneyQuestionType.choice) {
    const config = question.config as JourneyQuestionConfigMap[JourneyQuestionType.choice];
    if (config.options.length < 2) {
      return 'Must provide at least 2 choices';
    }
    if (config.options.find(o => !o.label)) {
      return 'All options must have a label';
    }
  }
}

export function isQuestionType<T extends JourneyQuestionType>(
  question: JourneyGraphNodeQuestion,
  type: T,
): question is JourneyGraphNodeQuestion<T> {
  return question.type === type;
}

/**
 * Given a step letter and number, generate a label for a step.
 */
export function makeStepLabel(stepLetter: number | null, stepIndex: number | null) {
  let letter = '';
  if (stepLetter) {
    letter = alphabet[stepLetter];
    if (stepLetter > 26) {
      const rotations = Math.floor(stepLetter / 26);
      letter = `${alphabet[rotations]}${alphabet[stepLetter % 26]}`;
      if (rotations > 26) {
        const rotationsBis = Math.floor(rotations / 26);
        letter = `${alphabet[rotationsBis]}${alphabet[rotations % 26]}${
          alphabet[stepLetter % 26]
        }`;
      }
    }
  }
  return `Step ${letter}${stepIndex}`;
}
const alphabet = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Given a journey graph and its root node, return a map of node id -> step label.
 */
export function makeStepLabelMap(journeyGraph: JourneyGraph, root: string) {
  const graph = journeyGraph.graph;
  const labelMap: Record<string, string> = {};

  function recursivelyAddLabels(nodeId: string, stepLetter: number, stepIndex: number) {
    labelMap[nodeId] = makeStepLabel(stepLetter, stepIndex);
    if (graph[nodeId]) {
      const nextNodes = Object.keys(graph[nodeId]);
      if (nextNodes.length === 1) {
        recursivelyAddLabels(nextNodes[0], stepLetter, stepIndex + 1);
      } else if (nextNodes.length > 1) {
        const node = journeyGraph.nodes[nodeId];
        // Since the set of nodes is a hash, we have to manually
        // handle nodes that branch off as iterating over the hash
        // would be non-deterministic.  The logic here must be the
        // same as in JourneyNodeBranch.
        if (isBranchNode(node) || isInboundChatNode(node)) {
          recursivelyAddLabels(
            node.data.trueChildName,
            stepLetter * 2 + 1,
            stepIndex + 1,
          );
          recursivelyAddLabels(
            node.data.falseChildName,
            stepLetter * 2 + 2,
            stepIndex + 1,
          );
        } else if (isQuestionNode(node) && node.data.childNodes) {
          const childCount = node.data.childNodes.length;
          node.data.childNodes.forEach((childId, index) => {
            recursivelyAddLabels(
              childId,
              stepLetter * childCount + index + 1,
              stepIndex + 1,
            );
          });
        } else {
          console.warn('unexpected node with multiple children:', node);
        }
      }
    }
  }
  recursivelyAddLabels(root, 0, 1);
  return labelMap;
}

/**
 * Get grouping label for variable name
 */
export function getVariableGroupingLabelByName(variableName: string) {
  if (
    [
      'country',
      'email',
      'location',
      'segments',
      'url',
      'url_title',
      'has_chatted',
      'ip_address',
      'us_state',
      'name',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'known_visitor',
    ].includes(variableName) ||
    variableName.startsWith('metadata__')
  ) {
    return 'Contacts & visitors';
  }
  if (
    variableName.startsWith('company') ||
    ['target_account', 'ideal_customer_profile_tier'].includes(variableName) ||
    variableName.startsWith('metadata_company__')
  ) {
    return 'Company';
  }
  if (['agents_available', 'groups_available', 'is_available'].includes(variableName)) {
    return 'Availability';
  }
  if (['question_choice'].includes(variableName)) {
    return 'Journey';
  }
  if (['page_scroll_y_percent', 'page_visits', 'time_on_page'].includes(variableName)) {
    return 'Analytics';
  }
  if (
    ['has_open_deal', 'deal_stage'].includes(variableName) ||
    variableName.includes('metadata_deal__')
  ) {
    return 'Deal';
  }
  if (variableName.startsWith('hubspot__')) {
    return 'HubSpot';
  }
  if (variableName.startsWith('outreach__')) {
    return 'Outreach';
  }
  if (variableName.startsWith('sixsense_')) {
    return '6sense';
  }
  if (variableName.startsWith('clearbit_')) {
    return 'Clearbit';
  }
  return 'Other';
}

/**
 * Clear out any fields that the server will reject for updates
 * @param localJourney
 * @returns partial Journey (with ID) for server update/create
 */
export function journeyForServerUpdate(
  localJourney: PartialJourneyWithId,
): JourneyUpdate {
  const latestVersion = localJourney.latestVersion
    ? omit(
        localJourney.latestVersion,
        'id',
        'dateUpdated',
        'dateCreated',
        'versionNumber',
      )
    : undefined;
  const journey = omit(localJourney, [
    'type',
    'hasChanged',
    'dateUpdated',
    'dateCreated',
    'description',
    'active',
  ]) as PartialJourneyWithId;
  return {
    ...journey,
    latestVersion,
    splitTestConfig:
      localJourney.splitTestConfig &&
      (omit(localJourney.splitTestConfig, [
        'journeyId',
        'variantAId',
        'variantBId',
      ]) as JourneySplitTestConfigUpdate),
  };
}

export function journeyFromTemplate(
  name: string,
  journeyTemplate: Journey,
): Omit<JourneyUpdate, 'id'> {
  const newJourney = journeyForServerUpdate(journeyTemplate);
  let graph: JourneyGraph | undefined = undefined;

  if (newJourney.latestVersion) {
    const graphScrubResult = scrubTemplateGraph(newJourney.latestVersion.graph);
    graph = graphScrubResult.journeyGraph;
  }

  const latestVersion = newJourney.latestVersion;
  if (graph && latestVersion) {
    latestVersion.graph = graph;
  }

  return {
    name,
    enabled: false,
    latestVersion: latestVersion,
  };
}

type SetGraph = ReturnType<typeof useActiveJourney>['setGraph'];
type SetAction = (x: React.SetStateAction<JourneyNodeAction>) => void;
/**
 * Re-usable calls for cancelling an action/node update
 * @param nodeName
 * @param graph
 * @param setGraph
 * @param setAction
 */
export function cancelUpdateAction(
  nodeName: string,
  graph: JourneyGraph,
  setGraph: SetGraph,
  setAction: SetAction,
) {
  setGraph(deleteUnknownNonLeafNode(nodeName, graph));
  setAction({ name: null, node: null });
}
